import React, { useEffect, useRef } from "react";
import { motion, useScroll, useTransform, useSpring, useInView } from "framer-motion";
import { ArrowRight, Sparkles, Brain, Zap, BarChart, ChevronRight, Play, Star, Shield, Cpu, Database, Cloud, Bot, Building, ShoppingCart, Heart, Truck, Car, Plane, GraduationCap, Gamepad2, Coffee } from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { ROUTE_PATHS, cn } from "@/lib/index";
import { services, caseStudies, stats } from "@/data/index";
import { ServiceCard, CaseStudyCard, StatCard } from "@/components/Cards";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";
import { AnimatedNetwork } from "@/components/AnimatedNetwork";
import { FloatingParticles } from "@/components/FloatingParticles";
import { NeuralNetworkBackground } from "@/components/NeuralNetworkBackground";

const Home = () => {
  const { scrollYProgress } = useScroll();
  const heroRef = useRef<HTMLDivElement>(null);
  const industriesRef = useRef<HTMLDivElement>(null);
  const agentsRef = useRef<HTMLDivElement>(null);
  
  const heroInView = useInView(heroRef, { once: false, amount: 0.3 });
  const industriesInView = useInView(industriesRef, { once: false, amount: 0.2 });
  const agentsInView = useInView(agentsRef, { once: false, amount: 0.2 });
  
  // Video background control - Fixed until AI Agents
  const [showVideo, setShowVideo] = React.useState(true);
  
  useEffect(() => {
    const handleScroll = () => {
      if (agentsRef.current) {
        const agentsRect = agentsRef.current.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Hide video when AI Agents section is about to appear (more generous threshold)
        setShowVideo(agentsRect.top > windowHeight * 0.5);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // No parallax effects - content scrolls normally over fixed video

  // Counter animation hook
  const useCountUp = (end: number, duration: number = 2000) => {
    const [count, setCount] = React.useState(0);
    const countRef = useRef<HTMLDivElement>(null);
    const inView = useInView(countRef, { once: true });

    useEffect(() => {
      if (!inView) return;

      let startTime: number;
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        setCount(Math.floor(progress * end));
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }, [inView, end, duration]);

    return { count, ref: countRef };
  };

  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section with Video Background - Globant Style */}
      <section 
        ref={heroRef}
        className="relative h-[85vh] flex items-center overflow-hidden bg-black"
      >
        {/* Fixed Video Background - Globant Style */}
        <div className={`fixed inset-0 z-0 transition-opacity duration-700 ${showVideo ? 'opacity-100' : 'opacity-0'}`}>
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://video001.blob.core.windows.net/videopag/Video5.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-black/40" />
        </div>


        {/* Content - Fixed Position Above Video */}
        <div className="relative z-50 container mx-auto px-4 min-h-screen flex items-center">
          <div className="max-w-4xl">
            {/* Animated Badge */}
            <motion.div 
              initial={{ opacity: 0, x: -20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ ...springPresets.gentle, delay: 0.2 }}
              whileHover={{ scale: 1.05, x: 5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/30 to-yellow-400/25 border border-primary/40 text-primary text-sm font-medium mb-8 backdrop-blur-md bg-white/10"
            >
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              >
                <Sparkles size={16} />
              </motion.div>
              <span>AI Studios</span>
            </motion.div>
            
            {/* Main Headline - Black with Yellow IA */}
            <motion.h1 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.smooth, delay: 0.3 }}
              className="text-5xl md:text-7xl font-bold tracking-tight mb-8 text-white leading-tight"
            >
              <span className="text-white">Industrias impulsadas por </span>
              <motion.span 
                className="bg-gradient-to-r from-primary via-yellow-300 to-yellow-500 bg-clip-text text-transparent"
                animate={{ 
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{ backgroundSize: "200% 200%" }}
              >
                IA
              </motion.span>
            </motion.h1>

            {/* Animated Subtitle */}
            <motion.p 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.smooth, delay: 0.5 }}
              className="text-xl md:text-2xl text-gray-200 mb-12 max-w-3xl leading-relaxed"
            >
              Obtén una ventaja competitiva en tu industria a través de soluciones comerciales impulsadas por IA con nuestros AI Studios
            </motion.p>

            {/* Enhanced CTA Button */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ ...springPresets.smooth, delay: 0.7 }}
            >
              <Link 
                to={ROUTE_PATHS.CONTACT} 
                className="group relative inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-xl overflow-hidden transition-all duration-300 shadow-2xl hover:shadow-primary/30"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "0%" }}
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10">Ponte en contacto</span>
                <motion.div
                  className="relative z-10"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ArrowRight size={20} />
                </motion.div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Industries Section - No Background, Video Shows Through */}
      <section 
        ref={industriesRef}
        className="py-16 relative z-40"
      >
        <div className="container mx-auto px-4 relative z-20">
          {/* Text Section with Own Background */}
          <div className="mb-12">
            <div className="max-w-5xl mx-auto text-center bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-lg">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900 leading-tight">
                Potencia el{' '}
                <span className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent">
                  crecimiento de tu negocio
                </span>{' '}
                con soluciones diseñadas para tu industria
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8 text-left">
                <div>
                  <p className="text-xl text-gray-700 leading-relaxed mb-4">
                    Con nuestra sólida experiencia intersectorial y nuestro enfoque impulsado por IA, desarrollamos estrategias personalizadas que abordan tus desafíos particulares.
                  </p>
                </div>
                
                <div>
                  <p className="text-xl text-gray-700 leading-relaxed mb-4">
                    Combinamos innovación tecnológica con conocimientos específicos del sector, lo que permite a tu empresa:
                  </p>
                  
                  <ul className="space-y-2 text-lg text-gray-600">
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      Maximizar la eficiencia
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      Adaptarse rápidamente
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-primary rounded-full"></div>
                      Mantener ventaja competitiva
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Industries Grid */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {[
              { 
                name: 'Banca', 
                desc: 'Ayudamos a los bancos a crecer y mejorar la experiencia del cliente con IA, analítica y plataformas digitales seguras.',
                icon: Building,
                bgClass: 'bg-gradient-to-br from-yellow-400/20 to-yellow-600/30',
                iconBg: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
                delay: 0
              },
              { 
                name: 'Retail', 
                desc: 'Aprovechamos la IA, los datos y la tecnología para aumentar la participación del cliente y optimizar inventarios.',
                icon: ShoppingCart,
                bgClass: 'bg-gradient-to-br from-amber-400/20 to-orange-500/30',
                iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
                delay: 0.1
              },
              { 
                name: 'Salud', 
                desc: 'Mejoramos la conexión entre la tecnología y las ciencias de la vida, combinando expertise médico con IA.',
                icon: Heart,
                bgClass: 'bg-gradient-to-br from-yellow-300/20 to-yellow-500/30',
                iconBg: 'bg-gradient-to-br from-yellow-300 to-yellow-500',
                delay: 0.2
              },
              { 
                name: 'Logística', 
                desc: 'Transformamos cadenas de suministro con IA predictiva, optimización de rutas y automatización de almacenes.',
                icon: Truck,
                bgClass: 'bg-gradient-to-br from-yellow-600/20 to-yellow-700/30',
                iconBg: 'bg-gradient-to-br from-yellow-600 to-yellow-700',
                delay: 0.3
              },
              { 
                name: 'Seguros', 
                desc: 'Revolucionamos la industria aseguradora con evaluación de riesgos automatizada y detección de fraudes.',
                icon: Shield,
                bgClass: 'bg-gradient-to-br from-yellow-500/20 to-amber-500/30',
                iconBg: 'bg-gradient-to-br from-yellow-500 to-amber-500',
                delay: 0.4
              },
              { 
                name: 'Telecomunicaciones', 
                desc: 'Modernizamos redes y experiencias de clientes con IA predictiva y optimización de infraestructura.',
                icon: Cpu,
                bgClass: 'bg-gradient-to-br from-amber-500/20 to-yellow-600/30',
                iconBg: 'bg-gradient-to-br from-amber-500 to-yellow-600',
                delay: 0.5
              },
              { 
                name: 'Manufactura', 
                desc: 'Implementamos Industria 4.0 con mantenimiento predictivo y control de calidad automatizado.',
                icon: Zap,
                bgClass: 'bg-gradient-to-br from-yellow-400/20 to-yellow-600/30',
                iconBg: 'bg-gradient-to-br from-yellow-400 to-yellow-600',
                delay: 0.6
              },
              { 
                name: 'Energía', 
                desc: 'Optimizamos la gestión energética con IA para redes inteligentes y predicción de demanda.',
                icon: Database,
                bgClass: 'bg-gradient-to-br from-yellow-500/20 to-amber-600/30',
                iconBg: 'bg-gradient-to-br from-yellow-500 to-amber-600',
                delay: 0.7
              }
            ].map((industry, idx) => (
              <motion.div 
                key={idx}
                variants={staggerItem}
                whileHover={{ 
                  scale: 1.05, 
                  y: -10,
                  rotateY: 5,
                  transition: { duration: 0.3 }
                }}
                whileTap={{ scale: 0.95 }}
                className="group p-8 rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 hover:border-primary/50 transition-all duration-500 hover:shadow-2xl hover:shadow-primary/20 cursor-pointer relative overflow-hidden hover:bg-white/20"
              >
                <motion.div 
                  className={`w-16 h-16 rounded-2xl ${industry.iconBg} flex items-center justify-center mb-6 shadow-lg relative z-10`}
                  whileHover={{ 
                    rotate: [0, -10, 10, 0],
                    scale: [1, 1.1, 1.1, 1],
                    transition: { duration: 0.6 }
                  }}
                >
                  <industry.icon className="w-8 h-8 text-white" />
                </motion.div>

                <motion.h3 
                  className="text-xl font-bold mb-4 text-white group-hover:text-primary transition-colors relative z-10"
                  whileHover={{ x: 5 }}
                >
                  {industry.name}
                </motion.h3>
                
                <motion.p 
                  className="text-gray-200 text-sm leading-relaxed mb-4 relative z-10"
                  whileHover={{ x: 3 }}
                >
                  {industry.desc}
                </motion.p>
                
                <motion.div
                  whileHover={{ x: 10, scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <ChevronRight className="w-5 h-5 text-primary relative z-10" />
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* AI Agents Section with Simplified Animations */}
      <section 
        ref={agentsRef}
        className="py-24 bg-gradient-to-b from-black to-gray-900 text-white relative overflow-hidden z-40"
      >
        {/* Simplified Background */}
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.TECH_ABSTRACT_2} 
            alt="AI Background"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Minimal Network Animation */}
        <AnimatedNetwork 
          className="opacity-15" 
          nodeCount={25}
          connectionDistance={150}
          speed={0.1}
          color="#F59E0B"
        />

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              <motion.span 
                className="bg-gradient-to-r from-primary to-yellow-400 bg-clip-text text-transparent"
                animate={{ 
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                style={{ backgroundSize: "200% 200%" }}
              >
                AI Agents
              </motion.span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl text-gray-300 max-w-4xl mx-auto"
            >
              Los Agentes de IA de Pryma Digital aprovechan algoritmos avanzados de última generación para automatizar tareas complejas, mejorar la toma de decisiones estratégicas e impulsar la eficiencia operacional en múltiples sectores industriales.
            </motion.p>
          </div>

          {/* Simplified AI Capabilities Grid */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                title: 'Automatización de Procesos Inteligente (RPA+)',
                desc: 'Combinamos RPA tradicional con IA para crear automatizaciones que aprenden y se adaptan, manejando procesos complejos que requieren toma de decisiones.',
                icon: Bot,
                features: ['Procesamiento de documentos no estructurados', 'Automatización de workflows complejos', 'Integración con sistemas legacy', 'Monitoreo y optimización continua']
              },
              {
                title: 'Agentes Conversacionales Empresariales',
                desc: 'Desarrollamos asistentes virtuales especializados que entienden el contexto de tu negocio y pueden ejecutar tareas complejas de manera autónoma.',
                icon: Brain,
                features: ['Integración con CRM y ERP', 'Procesamiento de lenguaje natural avanzado', 'Aprendizaje continuo del dominio', 'Escalabilidad enterprise']
              },
              {
                title: 'Análisis Predictivo y Business Intelligence',
                desc: 'Transformamos datos en insights accionables usando modelos de ML avanzados para predicción de tendencias y optimización de decisiones estratégicas.',
                icon: BarChart,
                features: ['Modelos predictivos personalizados', 'Dashboards interactivos en tiempo real', 'Análisis de sentimiento y tendencias', 'Recomendaciones automatizadas']
              },
              {
                title: 'Transformación Digital de Procesos',
                desc: 'Rediseñamos procesos de negocio integrando IA para maximizar eficiencia, reducir errores y acelerar la toma de decisiones críticas.',
                icon: Zap,
                features: ['Mapeo y optimización de procesos', 'Implementación de workflows inteligentes', 'Integración de sistemas heterogéneos', 'Métricas y KPIs automatizados']
              },
              {
                title: 'Plataformas de IA Personalizadas',
                desc: 'Construimos plataformas de IA a medida que se integran perfectamente con tu infraestructura existente y escalan con tu crecimiento.',
                icon: Cpu,
                features: ['Arquitectura cloud-native', 'APIs y microservicios', 'Seguridad y compliance enterprise', 'Mantenimiento y soporte 24/7']
              },
              {
                title: 'Consultoría Estratégica en IA',
                desc: 'Acompañamos tu transformación digital con estrategias de IA alineadas a objetivos de negocio, ROI medible y roadmaps de implementación.',
                icon: Star,
                features: ['Assessment de madurez digital', 'Roadmap de transformación IA', 'Change management y capacitación', 'Medición de ROI y KPIs']
              }
            ].map((capability, idx) => (
              <div 
                key={idx}
                className="group p-8 rounded-3xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700/50 hover:border-primary/50 transition-all duration-300 backdrop-blur-sm hover:bg-gradient-to-br hover:from-primary/5 hover:to-yellow-400/5"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-yellow-400 flex items-center justify-center mb-6">
                  <capability.icon className="w-8 h-8 text-black" />
                </div>

                <h3 className="text-xl font-bold mb-4 text-primary group-hover:text-yellow-400 transition-colors">
                  {capability.title}
                </h3>
                
                <p className="text-gray-300 leading-relaxed mb-6">
                  {capability.desc}
                </p>
                
                <ul className="space-y-2">
                  {capability.features.map((feature, featureIdx) => (
                    <li 
                      key={featureIdx} 
                      className="flex items-center gap-2 text-sm text-gray-400"
                    >
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </motion.div>

          {/* Enhanced CTA */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center mt-16"
          >
            <Link 
              to={ROUTE_PATHS.AGENTS}
              className="group relative inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-xl overflow-hidden transition-all duration-300 shadow-2xl hover:shadow-primary/30"
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600"
                initial={{ x: "-100%" }}
                whileHover={{ x: "0%" }}
                transition={{ duration: 0.3 }}
              />
              <span className="relative z-10">Explora nuestros AI Agents</span>
              <motion.div
                className="relative z-10"
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <ArrowRight className="w-5 h-5" />
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Case Studies Section with Animated Counters */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <motion.div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-32 h-32 rounded-full bg-gradient-to-br from-primary/5 to-yellow-400/5"
              style={{
                left: `${5 + i * 15}%`,
                top: `${10 + (i % 2) * 40}%`,
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.3, 0.6, 0.3],
                rotate: [0, 180, 360]
              }}
              transition={{
                duration: 10 + i * 2,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 1
              }}
            />
          ))}
        </motion.div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={springPresets.gentle}
              viewport={{ once: true }}
              className="text-4xl md:text-6xl font-bold mb-6 text-black"
            >
              Nuestra experiencia{' '}
              <motion.span 
                className="bg-gradient-to-r from-primary to-yellow-500 bg-clip-text text-transparent"
                animate={{ 
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{ backgroundSize: "200% 200%" }}
              >
                intersectorial
              </motion.span>
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ ...springPresets.gentle, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-4xl mx-auto"
            >
              Descubre cómo hemos ayudado a marcas líderes de diversos sectores a transformarse digitalmente, optimizar operaciones y alcanzar resultados excepcionales con IA.
            </motion.p>
          </div>

          {/* Enhanced Case Studies Grid */}
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            {[
              {
                title: 'Transformación Digital Bancaria',
                subtitle: 'Una plataforma de IA personalizada para optimizar la experiencia de clientes corporativos y automatizar procesos de riesgo',
                metric: 85,
                suffix: '% más eficiente',
                image: IMAGES.ANALYTICS_1,
                industry: 'Financial Services',
                results: ['Reducción 60% tiempo de procesamiento', 'Incremento 40% satisfacción cliente', 'ROI 320% en primer año']
              },
              {
                title: 'Automatización Inteligente en Retail',
                subtitle: 'Sistema de IA para predicción de demanda, optimización de inventario y personalización de experiencia de compra',
                metric: 300,
                suffix: '% ROI',
                image: IMAGES.AUTOMATION_6,
                industry: 'Retail',
                results: ['Reducción 25% stock obsoleto', 'Incremento 35% ventas', 'Optimización 90% cadena suministro']
              },
              {
                title: 'Plataforma de Agentes Conversacionales',
                subtitle: 'Red de agentes de IA especializados para atención al cliente 24/7 con capacidades de resolución autónoma',
                metric: 92,
                suffix: '% satisfacción',
                image: IMAGES.ANALYTICS_4,
                industry: 'Telecommunications',
                results: ['Resolución 80% consultas automática', 'Reducción 50% tiempo respuesta', 'Ahorro 2M USD anuales']
              }
            ].map((caseStudy, idx) => {
              const { count, ref } = useCountUp(caseStudy.metric, 2500);
              
              return (
                <motion.div 
                  key={idx}
                  variants={staggerItem}
                  whileHover={{ 
                    scale: 1.03, 
                    y: -10,
                    rotateY: 2,
                    transition: { duration: 0.3 }
                  }}
                  className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 relative"
                >
                  <div className="relative h-48 overflow-hidden">
                    <motion.img 
                      src={caseStudy.image} 
                      alt={caseStudy.title}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    
                    {/* Animated Industry Badge */}
                    <motion.div 
                      className="absolute top-4 left-4 px-3 py-1 bg-gradient-to-r from-primary to-yellow-400 text-black text-sm font-semibold rounded-full"
                      whileHover={{ scale: 1.05, x: 5 }}
                    >
                      {caseStudy.industry}
                    </motion.div>
                    
                    {/* Animated Metric */}
                    <motion.div 
                      ref={ref}
                      className="absolute bottom-4 right-4 text-white font-bold text-lg"
                      whileHover={{ scale: 1.1 }}
                    >
                      <motion.span
                        className="text-2xl"
                        animate={{ 
                          textShadow: [
                            "0 0 5px rgba(245, 158, 11, 0.5)",
                            "0 0 20px rgba(245, 158, 11, 0.8)",
                            "0 0 5px rgba(245, 158, 11, 0.5)"
                          ]
                        }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        {count}
                      </motion.span>
                      {caseStudy.suffix}
                    </motion.div>
                  </div>
                  
                  <div className="p-6">
                    <motion.h3 
                      className="text-xl font-bold mb-3 text-black group-hover:text-primary transition-colors"
                      whileHover={{ x: 5 }}
                    >
                      {caseStudy.title}
                    </motion.h3>
                    <motion.p 
                      className="text-gray-600 text-sm leading-relaxed mb-4"
                      whileHover={{ x: 3 }}
                    >
                      {caseStudy.subtitle}
                    </motion.p>
                    <div className="space-y-2">
                      {caseStudy.results.map((result, resultIdx) => (
                        <motion.div 
                          key={resultIdx} 
                          className="flex items-center gap-2 text-xs text-gray-500"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          transition={{ delay: resultIdx * 0.1 }}
                          whileHover={{ x: 5, color: "#F59E0B" }}
                        >
                          <motion.div 
                            className="w-1.5 h-1.5 bg-primary rounded-full"
                            animate={{ scale: [1, 1.3, 1] }}
                            transition={{ 
                              duration: 2, 
                              repeat: Infinity, 
                              delay: resultIdx * 0.3 
                            }}
                          />
                          {result}
                        </motion.div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Enhanced Industry Tags */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-4 mb-12"
          >
            {['Financial Services', 'Healthcare & Life Sciences', 'Media & Entertainment', 'Automotive', 'Airlines', 'Education', 'Gaming', 'Sports', 'Hospitality & Leisure', 'Retail'].map((industry, idx) => (
              <motion.span 
                key={idx}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gradient-to-r hover:from-primary hover:to-yellow-400 hover:text-black transition-all cursor-pointer"
                whileHover={{ 
                  scale: 1.05, 
                  y: -2,
                  transition: { duration: 0.2 }
                }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                {industry}
              </motion.span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Enhanced Final CTA Section */}
      <section className="py-24 bg-gradient-to-br from-black via-gray-900 to-black text-white relative overflow-hidden">
        {/* Multiple Animated Network Layers */}
        <AnimatedNetwork 
          className="opacity-10" 
          nodeCount={30}
          connectionDistance={250}
          speed={0.1}
          color="#F59E0B"
        />
        <AnimatedNetwork 
          className="opacity-5" 
          nodeCount={20}
          connectionDistance={300}
          speed={0.15}
          color="#EAB308"
        />
        
        {/* Floating Particles */}
        <FloatingParticles 
          className="opacity-30" 
          particleCount={40}
        />
        
        {/* Background Pattern */}
        <motion.div 
          className="absolute inset-0 opacity-5"
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        >
          <img 
            src={IMAGES.INDUSTRIES_GRID} 
            alt="Background Pattern"
            className="w-full h-full object-cover"
          />
        </motion.div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={springPresets.gentle}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.h2 
              className="text-5xl md:text-7xl font-bold mb-8"
              animate={{ 
                textShadow: [
                  "0 0 20px rgba(245, 158, 11, 0.3)",
                  "0 0 40px rgba(245, 158, 11, 0.6)",
                  "0 0 20px rgba(245, 158, 11, 0.3)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              Sé parte de la{' '}
              <motion.span 
                className="bg-gradient-to-r from-primary via-yellow-300 to-yellow-500 bg-clip-text text-transparent"
                animate={{ 
                  backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"]
                }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                style={{ backgroundSize: "200% 200%" }}
              >
                revolución de la IA
              </motion.span>{' '}
              ahora
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              Quiero suscribirme con mi dirección de correo electrónico para recibir mensajes de Pryma Digital sobre novedades, recursos valiosos y consejos útiles sobre transformación digital e IA.
            </motion.p>
            
            {/* Enhanced Newsletter Form */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <motion.input 
                type="email" 
                placeholder="Tu email corporativo"
                className="flex-1 px-6 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-primary backdrop-blur-sm transition-all duration-300"
                whileFocus={{ 
                  scale: 1.02,
                  borderColor: "#F59E0B",
                  boxShadow: "0 0 20px rgba(245, 158, 11, 0.3)"
                }}
              />
              <motion.button 
                className="px-8 py-4 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold rounded-xl transition-all duration-300 shadow-2xl relative overflow-hidden"
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 0 30px rgba(245, 158, 11, 0.5)"
                }}
                whileTap={{ scale: 0.95 }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "0%" }}
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10">Suscribirse</span>
              </motion.button>
            </motion.div>
            
            <motion.p 
              className="text-sm text-gray-400 mb-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Al completar este formulario, aceptas que estás de acuerdo con la política de confidencialidad de Pryma Digital.
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <Link 
                to={ROUTE_PATHS.CONTACT}
                className="group relative inline-flex items-center gap-3 px-12 py-6 bg-gradient-to-r from-primary via-yellow-400 to-yellow-500 text-black font-bold text-xl rounded-xl overflow-hidden transition-all duration-300 shadow-2xl"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-600"
                  initial={{ x: "-100%" }}
                  whileHover={{ x: "0%" }}
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10">Veamos cómo podemos hacer que suceda</span>
                <motion.div
                  className="relative z-10"
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <ArrowRight className="w-6 h-6" />
                </motion.div>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;